import { useEffect, useState } from 'react'
import { useGatedBook } from './useGatedBook'
import {
  readRoundMedia,
  readSharedRoundMedia,
  type RoundMediaItem,
} from '../lib/roundMedia'

const NO_MEDIA: readonly RoundMediaItem[] = []

export interface RoundMediaBook {
  media: readonly RoundMediaItem[]
  isLoading: boolean
  error: string | null
  membershipRequired: boolean
  refresh: () => Promise<void>
}

/** Gallery metadata for the member round page. */
export function useRoundMedia(roundId: string, enabled: boolean): RoundMediaBook {
  const book = useGatedBook<RoundMediaItem[]>({
    key: `/golf/rounds/${roundId}/media`,
    enabled,
    read: () => readRoundMedia(roundId).then((payload) => payload.media),
    whenFaulty: 'Could not open this round’s media.',
  })

  return {
    media: book.data ?? NO_MEDIA,
    isLoading: book.isLoading,
    error: book.error,
    membershipRequired: book.membershipRequired,
    refresh: book.refresh,
  }
}

interface SharedRoundMediaAnswer {
  token: string
  media: readonly RoundMediaItem[]
  dead: boolean
}

export interface SharedRoundMediaBook {
  media: readonly RoundMediaItem[]
  isLoading: boolean
  /** Every failed token-scoped read is the same refusal, as on the round. */
  dead: boolean
}

/**
 * Public-token gallery metadata. It is enabled only after the parent shared
 * round opens, reads no session, and never distinguishes token failure modes.
 */
export function useSharedRoundMedia(
  token: string,
  enabled: boolean,
): SharedRoundMediaBook {
  const [answer, setAnswer] = useState<SharedRoundMediaAnswer | null>(null)

  useEffect(() => {
    if (!enabled) return
    let active = true

    void readSharedRoundMedia(token)
      .then((payload) => {
        if (active) setAnswer({ token, media: payload.media, dead: false })
      })
      .catch(() => {
        if (active) setAnswer({ token, media: NO_MEDIA, dead: true })
      })

    return () => {
      active = false
    }
  }, [enabled, token])

  const settled = enabled && answer?.token === token ? answer : null

  return {
    media: settled?.media ?? NO_MEDIA,
    isLoading: enabled && settled === null,
    dead: settled?.dead ?? false,
  }
}
